import type { ReactNode } from 'react';
import { BACKUP_POLICIES } from '@cp/shared';
import { Select } from '@/components/ui';
import type { NodeView, TemplateVersionView } from '@/types/api';
import { BACKUP_POLICY_LABELS, label } from '@/utils/labels';

/**
 * The pickers a new site's forms share — creating a site and importing one:
 * which template version, which server, which backup policy.
 */

export function TemplateVersionSelect({
  id,
  value,
  onChange,
  versions,
  loading,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (id: string) => void;
  readonly versions: readonly TemplateVersionView[];
  readonly loading: boolean;
}): ReactNode {
  return (
    <Select
      id={id}
      label="Шаблон"
      value={value}
      onChange={onChange}
      disabled={versions.length === 0}
      placeholder={loading ? 'Загрузка…' : 'Нет активных шаблонов'}
      options={versions.map((version) => ({
        value: version.id,
        label: `${version.name} ${version.version}`,
        hint: version.imageDigest.slice(0, 19),
      }))}
    />
  );
}

/** A server the site goes to, or the scheduler's choice. Only servers taking new sites are offered. */
export function NodeSelect({
  id,
  value,
  onChange,
  nodes,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (id: string) => void;
  readonly nodes: readonly NodeView[];
}): ReactNode {
  return (
    <Select
      id={id}
      label="Сервер"
      value={value}
      onChange={onChange}
      options={[
        { value: '', label: 'Автоматически', hint: 'Наименее загруженный подходящий' },
        ...nodes.map((node) => ({
          value: node.id,
          label: node.name,
          hint: `${node.siteCount} сайт(ов) · занято ${Math.round((node.usedMemoryMb / Math.max(1, node.totalMemoryMb)) * 100)}% памяти`,
        })),
      ]}
    />
  );
}

/** The policies a site can be given; `custom` is not set from a form. */
export type SitePolicy = keyof typeof BACKUP_POLICIES;
const POLICIES = Object.keys(BACKUP_POLICIES) as SitePolicy[];

function scheduleOf(policy: SitePolicy): string {
  const rule = BACKUP_POLICIES[policy];
  return rule.intervalHours === 1 ? 'каждый час' : `каждые ${rule.intervalHours} ч`;
}

export function BackupPolicySelect({
  id,
  value,
  onChange,
}: {
  readonly id: string;
  readonly value: SitePolicy;
  readonly onChange: (policy: SitePolicy) => void;
}): ReactNode {
  return (
    <Select
      id={id}
      label="Политика копий"
      value={value}
      onChange={onChange}
      options={POLICIES.map((policy) => ({
        value: policy,
        label: label(BACKUP_POLICY_LABELS, policy),
        hint: scheduleOf(policy),
      }))}
    />
  );
}
